import React, { useState, useEffect } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import axios from "axios";
import { baseurl } from "../../baseurl";
import Swal from "sweetalert2";
import { FiMapPin, FiShield, FiAlertCircle, FiInfo } from "react-icons/fi";

function PincodeManager() {
  const [statesList, setStatesList] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [citiesList, setCitiesList] = useState([]);
  const [pincodesMap, setPincodesMap] = useState({});
  
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingPincodes, setLoadingPincodes] = useState({});

  useEffect(() => { fetchStates(); }, []);

  const fetchStates = async () => {
    setLoadingStates(true);
    try {
      const res = await axios.get(`${baseurl}getAllStates`);
      if (res.data.success) setStatesList(res.data.states);
    } catch (err) { console.error(err); }
    finally { setLoadingStates(false); }
  };

  const handleStateChange = async (stateId) => {
    setSelectedState(stateId);
    setCitiesList([]);
    setPincodesMap({});
    if (!stateId) return;

    setLoadingCities(true);
    try {
      const res = await axios.get(`${baseurl}getCitiesByState/${stateId}`);
      if (res.data.success) setCitiesList(res.data.cities);
    } catch (err) { console.error(err); }
    finally { setLoadingCities(false); }
  };

  const toggleState = async (stateId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      const res = await axios.put(`${baseurl}toggleStateStatus/${stateId}`, { is_active: newStatus });
      if (res.data.success) {
        setStatesList(prev => prev.map(s => s.id == stateId ? { ...s, is_active: newStatus } : s));
        Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: "State Status Updated", showConfirmButton: false, timer: 2000 });
      }
    } catch (err) { Swal.fire("Error", "Update failed", "error"); }
  };

  const toggleCity = async (e, cityId, currentStatus) => {
    e.stopPropagation();
    try {
      const newStatus = !currentStatus;
      const res = await axios.put(`${baseurl}toggleCityStatus/${cityId}`, { is_active: newStatus });
      if (res.data.success) {
        setCitiesList(prev => prev.map(c => c.id === cityId ? { ...c, is_active: newStatus } : c));
      }
    } catch (err) { Swal.fire("Error", "Update failed", "error"); }
  };

  const toggleCityPincodes = async (cityId) => {
    if (pincodesMap[cityId]) return;
    setLoadingPincodes(prev => ({ ...prev, [cityId]: true }));
    try {
      const res = await axios.get(`${baseurl}getPincodesByDistrict/${cityId}`);
      if (res.data.success) setPincodesMap(prev => ({ ...prev, [cityId]: res.data.pincodes }));
    } catch (err) { console.error(err); }
    finally { setLoadingPincodes(prev => ({ ...prev, [cityId]: false })); }
  };

  const currentStateObj = statesList.find(s => s.id == selectedState);

  return (
    <>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper bg-light min-vh-100">
        <div className="main-content p-3 p-lg-4">
          
          {/* Header & Explainer Area */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 className="fw-bold mb-0">Regional Service Manager</h5>
              <p className="extra-small text-muted">Control logistics serviceability by State, City, and Pincode levels.</p>
            </div>
          </div>

          <div className="row g-3">
            {/* Left: Configuration Panel */}
            <div className="col-lg-4">
              <div className="card border-0 shadow-sm p-3 sticky-top" style={{ top: "80px", zIndex: 10 }}>
                <h6 className="fw-bold mb-3 small d-flex align-items-center">
                  <FiShield className="me-2 text-primary"/> Global Controls
                </h6>
                
                <div className="mb-3">
                  <label className="extra-small fw-bold text-uppercase text-muted mb-1">Target State</label>
                  <select className="form-select form-select-sm shadow-none" value={selectedState} onChange={(e) => handleStateChange(e.target.value)}>
                    <option value="">-- Choose State --</option>
                    {statesList.map((s) => (
                      <option key={s.id} value={s.id}>{s.state_name}</option>
                    ))}
                  </select>
                </div>

                {selectedState && (
                  <div className={`p-2 rounded border ${currentStateObj?.is_active ? 'bg-success-light border-success' : 'bg-danger-light border-danger'}`}>
                    <div className="form-check form-switch px-5 py-1">
                      <input 
                        className="form-check-input" 
                        type="checkbox" 
                        checked={currentStateObj?.is_active || false} 
                        onChange={() => toggleState(selectedState, currentStateObj.is_active)}
                      />
                      <label className="form-check-label small fw-bold">
                        {currentStateObj?.is_active ? "State Service Active" : "State Service Blocked"}
                      </label>
                    </div>
                  </div>
                )}

                <div className="mt-4 p-3 bg-light rounded border">
                  <p className="extra-small mb-1 text-primary fw-bold d-flex align-items-center"><FiInfo className="me-1"/> Logic Notes:</p>
                  <ul className="extra-small text-muted ps-3 mb-0">
                    <li className="mb-1"><b>State Block:</b> Disables all cities and pincodes within this state regardless of individual settings.</li>
                    <li className="mb-1"><b>City Toggle:</b> Use switches in the list to enable/disable specific municipal zones.</li>
                    <li><b>Pincodes:</b> Click a city name to audit the associated postal codes.</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Right: City Manifest */}
            <div className="col-lg-8">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white py-3 border-0 d-flex justify-content-between align-items-center">
                  <h6 className="mb-0 fw-bold small">City Serviceability List</h6>
                  <span className="badge bg-light text-dark border fw-normal">{citiesList.length} Cities Found</span>
                </div>
                
                <div className="card-body p-0">
                  {loadingCities ? (
                    <div className="text-center py-5"><div className="spinner-border spinner-border-sm text-primary"></div></div>
                  ) : !selectedState ? (
                    <div className="text-center py-5 text-muted">
                      <FiAlertCircle size={30} className="mb-2 opacity-25"/>
                      <p className="small">Please select a state to manage cities.</p>
                    </div>
                  ) : (
                    <div className="accordion accordion-flush" id="cityAccordion">
                      {citiesList.map((city) => (
                        <div className="accordion-item border-bottom" key={city.id}>
                          <h2 className="accordion-header">
                            <div className="accordion-button collapsed py-2 px-3 d-flex align-items-center" 
                                 style={{ cursor: 'pointer' }}
                                 onClick={() => toggleCityPincodes(city.id)}
                                 data-bs-toggle="collapse" 
                                 data-bs-target={`#city-${city.id}`}>
                              
                              <div className="d-flex align-items-center flex-grow-1">
                                <FiMapPin className="me-2 text-muted" size={14}/>
                                <span className={`small fw-semibold ${!city.is_active && 'text-decoration-line-through text-muted'}`}>
                                  {city.district_name}
                                </span>
                              </div>

                              <div className="form-check form-switch me-3" onClick={(e) => e.stopPropagation()}>
                                <input 
                                  className="form-check-input" 
                                  type="checkbox" 
                                  checked={city.is_active} 
                                  onChange={(e) => toggleCity(e, city.id, city.is_active)}
                                />
                              </div>
                            </div>
                          </h2>
                          
                          <div id={`city-${city.id}`} className="accordion-collapse collapse" data-bs-parent="#cityAccordion">
                            <div className="accordion-body bg-light-gray py-2">
                                <p className="extra-small text-muted mb-2 fw-bold">Covered Pincodes:</p>
                              {loadingPincodes[city.id] ? (
                                <div className="text-center"><div className="spinner-border spinner-border-sm"></div></div>
                              ) : (
                                <div className="d-flex flex-wrap gap-1">
                                  {pincodesMap[city.id]?.map((p) => (
                                    <span key={p.id} className="badge bg-white text-dark border-0 shadow-xs px-2 py-1 fw-normal" style={{ fontSize: '10px' }}>
                                      {p.pincode}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <style>{`
        .extra-small { font-size: 11px; }
        .bg-success-light { background-color: #f0fdf4; }
        .bg-danger-light { background-color: #fef2f2; }
        .bg-light-gray { background-color: #f8f9fa; }
        .shadow-xs { box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
        .accordion-button::after { width: 1rem; height: 1rem; background-size: 1rem; }
        .form-check-input { cursor: pointer; }
        .sticky-top { transition: all 0.3s ease; }
      `}</style>
    </>
  );
}

export default PincodeManager;